import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { InlineDatePickerField, InlineDatePickerPanel } from "../DatePickerField";
import {
  addPartialDeposit,
  formatUsd,
  isPartialPlan,
  normalizePaymentPlan,
  PAYMENT_PLAN_LABELS,
  removePartialDeposit,
  rescaleDepositsForPlan,
  roundMoney,
  SERVICE_TYPE_LABELS,
  syncDepositsToPaymentPlan,
} from "../../data/revenueData";
import { canonicalOffering } from "../../config/offeringPhases";
import { createRevenueInvoice } from "../../lib/revenueApi";
import { api, formatApiDetail } from "../../lib/api";
import { isInvoiceEligibleClient } from "../../lib/engagementStages";

const DIALOG_CONTROL = "t-input !rounded-xl !bg-white w-full min-h-[45px] h-[45px] text-sm shadow-none";

const DIALOG_SELECT =
  "t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10 text-sm shadow-none";

const DIALOG_SELECT_NO_ARROW = `${DIALOG_SELECT} [&>svg]:hidden`;

const DIALOG_TEXTAREA =
  "t-textarea !rounded-xl !bg-white w-full min-h-[88px] text-sm shadow-none resize-y";

const DIALOG_BTN = "!rounded-xl";

const DATE_PICKER_TRIGGER = "!rounded-xl !bg-white !min-h-[45px] !h-[45px] !py-0";

const PAYMENT_COMPACT_INPUT =
  "t-input !rounded-xl bg-white w-full !min-h-[36px] !h-9 text-sm shadow-none tabular-nums";

const PAYMENT_MODE_GROUP =
  "inline-flex h-9 shrink-0 items-center rounded-xl border border-[var(--t-border)] bg-[#F4F5F7] p-0.5";

function paymentModeButtonClass(active) {
  return [
    "h-full min-w-[2.75rem] rounded-[calc(var(--t-event-pill-radius)-2px)] px-3 text-xs font-medium transition-colors",
    active
      ? "bg-white text-[var(--t-text)] shadow-sm"
      : "text-[var(--t-meta)] hover:text-[var(--t-text)]",
  ].join(" ");
}

function formatPercentDisplay(value) {
  const pct = Number(value) || 0;
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1).replace(/\.0$/, "")}%`;
}

const SERVICE_TYPE_OPTIONS = Object.entries(SERVICE_TYPE_LABELS).map(([tier, label]) => ({
  tier,
  label,
}));

const todayIso = () => new Date().toISOString().slice(0, 10);

function serviceTierForClient(client) {
  const tier = canonicalOffering(client?.service_tier);
  return SERVICE_TYPE_LABELS[tier] ? tier : "operational_ecosystem";
}

function serviceLabelForTier(tier) {
  return SERVICE_TYPE_LABELS[tier] || SERVICE_TYPE_LABELS.operational_ecosystem;
}

export default function InvoiceCreateDialog({ open, onOpenChange, onCreated, defaultClientId }) {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientId, setClientId] = useState("");
  const [amount, setAmount] = useState("");
  const [issued, setIssued] = useState(todayIso);
  const [due, setDue] = useState(todayIso);
  const [serviceTier, setServiceTier] = useState("operational_ecosystem");
  const [serviceDescription, setServiceDescription] = useState("");
  const [paymentPlan, setPaymentPlan] = useState("full");
  const [deposits, setDeposits] = useState([]);
  const [depositInputModes, setDepositInputModes] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openDateField, setOpenDateField] = useState(null);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId) || null,
    [clients, clientId],
  );

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const { data } = await api.get("/admin/clients");
      const rows = (Array.isArray(data) ? data : data?.clients || [])
        .filter(isInvoiceEligibleClient)
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setClients(rows);
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not load clients."));
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadClients();
    const today = todayIso();
    setClientId(defaultClientId || "");
    setAmount("");
    setIssued(today);
    setDue(today);
    setServiceTier("operational_ecosystem");
    setServiceDescription("");
    setPaymentPlan("full");
    setDeposits([]);
    setDepositInputModes({});
    setError("");
    setOpenDateField(null);
  }, [open, loadClients, defaultClientId]);

  useEffect(() => {
    if (!open || !defaultClientId || !clients.length) return;
    const client = clients.find((c) => c.id === defaultClientId);
    if (!client) return;
    setClientId(defaultClientId);
    setServiceTier(serviceTierForClient(client));
    if (client.rate != null && client.rate !== "") {
      setAmount(String(client.rate));
    }
  }, [open, defaultClientId, clients]);

  const handleClientChange = (nextId) => {
    setClientId(nextId);
    const client = clients.find((c) => c.id === nextId);
    if (!client) return;
    setServiceTier(serviceTierForClient(client));
    if (client.rate != null && client.rate !== "") {
      setAmount(String(client.rate));
    }
  };

  const handlePaymentPlanChange = (plan) => {
    const normalized = normalizePaymentPlan(plan);
    setPaymentPlan(normalized);
    const parsed = parseFloat(amount) || 0;
    const issuedDate = issued || todayIso();
    setDeposits((prev) =>
      syncDepositsToPaymentPlan(prev, parsed, normalized, issuedDate, due || issuedDate),
    );
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    const parsed = parseFloat(value);
    if (!parsed || parsed <= 0) return;
    setDeposits((prev) =>
      rescaleDepositsForPlan(prev, parsed, paymentPlan, issued || todayIso()),
    );
  };

  const updateDeposit = (id, field, value) => {
    const parsedAmount = parseFloat(amount) || 0;
    setDeposits((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d;
        if (field === "percent") {
          const pct = parseFloat(value) || 0;
          const rowAmount = parsedAmount > 0 ? roundMoney((parsedAmount * pct) / 100) : 0;
          return { ...d, percent: pct, amount: rowAmount };
        }
        if (field === "amount") {
          const rowAmount = parseFloat(value) || 0;
          const pct = parsedAmount > 0 ? roundMoney((rowAmount / parsedAmount) * 100) : 0;
          return { ...d, amount: rowAmount, percent: pct };
        }
        return { ...d, [field]: value };
      }),
    );
  };

  const handleAddPayment = () => {
    setDeposits((prev) => addPartialDeposit(prev, parseFloat(amount) || 0, issued || todayIso()));
  };

  const handleRemovePayment = (depId) => {
    setDeposits((prev) =>
      removePartialDeposit(prev, depId, parseFloat(amount) || 0),
    );
  };

  const handleIssuedChange = (next) => {
    setIssued(next);
  };

  const partial = isPartialPlan(paymentPlan);

  const getDepositInputMode = (depId) => depositInputModes[depId] || "percent";

  const setDepositInputMode = (depId, mode) => {
    setDepositInputModes((prev) => ({ ...prev, [depId]: mode }));
  };

  const buildDepositPayload = () =>
    deposits.map((d, i) => ({
      label: `Payment ${i + 1}`,
      percent: parseFloat(d.percent) || 0,
      amount: parseFloat(d.amount) || 0,
      due: (d.due || issued || todayIso()).slice(0, 10),
    }));

  const handleCreate = async () => {
    if (!clientId) {
      setError("Select a client.");
      return;
    }
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      setError("Enter a valid invoice amount.");
      return;
    }
    if (!issued) {
      setError("Issue date is required.");
      return;
    }
    if (partial) {
      if (deposits.length < 2) {
        setError("Add at least two payments for a partial schedule.");
        return;
      }
      const scheduled = roundMoney(deposits.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0));
      if (Math.abs(scheduled - parsedAmount) > 0.02) {
        setError("Payment amounts must add up to the invoice total.");
        return;
      }
    }

    setSaving(true);
    setError("");
    try {
      const invoice = await createRevenueInvoice({
        client_id: clientId,
        amount: parsedAmount,
        issued,
        due: due || issued,
        service_tier: serviceTier,
        invoice_service_title: serviceLabelForTier(serviceTier),
        invoice_service_description: serviceDescription.trim() || undefined,
        payment_plan: paymentPlan,
        deposits: partial ? buildDepositPayload() : undefined,
      });
      toast.success(`Invoice ${invoice.invoiceNumber} created`);
      onCreated?.(invoice);
      onOpenChange(false);
    } catch (e) {
      setError(formatApiDetail(e.response?.data?.detail, "Could not create invoice."));
    } finally {
      setSaving(false);
    }
  };

  const reduce = useReducedMotion();
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/25 p-0 sm:p-4"
      onClick={() => onOpenChange(false)}
      data-testid="invoice-create-dialog"
    >
      <motion.aside
        initial={reduce ? false : { x: 28, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="neo-panel neo-drawer flex h-full w-full max-w-md flex-col overflow-hidden rounded-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--t-border)] px-6 py-5">
          <div className="min-w-0">
            <div className="label-eyebrow mb-2">Revenue</div>
            <div className="font-heading truncate text-2xl tracking-tight !font-extralight">
              Add invoice
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-xl p-2 text-[var(--t-meta)] hover:bg-black/[0.04] hover:text-[var(--t-text)]"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="t-scroll-soft flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <Label className="label-eyebrow">Client</Label>
            <Select value={clientId} onValueChange={handleClientChange} disabled={loadingClients || saving}>
              <SelectTrigger className={`${DIALOG_SELECT_NO_ARROW} mt-1.5`} data-testid="invoice-create-client">
                <SelectValue placeholder={loadingClients ? "Loading clients…" : "Select client"} />
              </SelectTrigger>
              <SelectContent className="z-[60] rounded-[var(--t-event-pill-radius)]">
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="invoice-create-amount" className="label-eyebrow">
              Amount (USD)
            </Label>
            <input
              id="invoice-create-amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              className={`${DIALOG_CONTROL} mt-1.5`}
              data-testid="invoice-create-amount"
            />
            {selectedClient?.rate != null ? (
              <p className="t-meta text-xs mt-1">Client rate: ${Number(selectedClient.rate).toLocaleString()}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Issued</Label>
              <div className="mt-1.5">
                <InlineDatePickerField
                  value={issued}
                  onChange={handleIssuedChange}
                  placeholder="Select date"
                  triggerClassName={DATE_PICKER_TRIGGER}
                  expanded={openDateField === "issued"}
                  onExpandedChange={(next) => setOpenDateField(next ? "issued" : null)}
                  detachPanel
                  data-testid="invoice-create-issued"
                />
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Due</Label>
              <div className="mt-1.5">
                <InlineDatePickerField
                  value={due}
                  onChange={setDue}
                  placeholder="Select date"
                  triggerClassName={DATE_PICKER_TRIGGER}
                  expanded={openDateField === "due"}
                  onExpandedChange={(next) => setOpenDateField(next ? "due" : null)}
                  detachPanel
                  data-testid="invoice-create-due"
                />
              </div>
            </div>
          </div>
          {openDateField === "issued" ? (
            <InlineDatePickerPanel
              value={issued}
              onChange={handleIssuedChange}
              onClose={() => setOpenDateField(null)}
              className="mt-1"
              data-testid="invoice-create-issued-panel"
            />
          ) : null}
          {openDateField === "due" ? (
            <InlineDatePickerPanel
              value={due}
              onChange={setDue}
              onClose={() => setOpenDateField(null)}
              className="mt-1"
              data-testid="invoice-create-due-panel"
            />
          ) : null}

          <div>
            <Label className="label-eyebrow">Service type</Label>
            <Select
              key={clientId || "no-client"}
              value={serviceTier}
              onValueChange={setServiceTier}
              disabled={saving}
            >
              <SelectTrigger className={`${DIALOG_SELECT_NO_ARROW} mt-1.5`} data-testid="invoice-create-service">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[60] rounded-[var(--t-event-pill-radius)]">
                {SERVICE_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.tier} value={opt.tier}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="invoice-create-description" className="label-eyebrow">
              Service description
            </Label>
            <textarea
              id="invoice-create-description"
              rows={3}
              value={serviceDescription}
              onChange={(e) => setServiceDescription(e.target.value)}
              className={`${DIALOG_TEXTAREA} mt-1.5`}
              data-testid="invoice-create-description"
            />
          </div>

          <div>
            <Label className="label-eyebrow">Payment schedule</Label>
            <Select value={paymentPlan} onValueChange={handlePaymentPlanChange} disabled={saving}>
              <SelectTrigger className={`${DIALOG_SELECT_NO_ARROW} mt-1.5`} data-testid="invoice-create-plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[60] rounded-[var(--t-event-pill-radius)]">
                <SelectItem value="full">{PAYMENT_PLAN_LABELS.full}</SelectItem>
                <SelectItem value="partial">{PAYMENT_PLAN_LABELS.partial}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {partial ? (
            <div>
              <ul className="space-y-2" data-testid="invoice-create-deposits">
                {deposits.map((dep, index) => {
                  const inputMode = getDepositInputMode(dep.id);
                  const parsedInvoiceAmount = parseFloat(amount) || 0;
                  return (
                  <li
                    key={dep.id}
                    className="rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] p-3 bg-[#FAFBFC] space-y-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div
                        className="text-sm font-heading !font-light"
                        data-testid={`invoice-create-payment-label-${dep.id}`}
                      >
                        Payment {index + 1}
                      </div>
                      {deposits.length > 2 ? (
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--t-event-pill-radius)] text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                          onClick={() => handleRemovePayment(dep.id)}
                          aria-label={`Remove payment ${index + 1}`}
                          data-testid={`invoice-create-remove-payment-${dep.id}`}
                        >
                          <Trash2 size={14} strokeWidth={1.4} />
                        </button>
                      ) : null}
                    </div>

                    <div className="flex items-end gap-2">
                      <div className="shrink-0">
                        <Label className="t-meta text-[10px] block mb-1">Enter as</Label>
                        <div className={PAYMENT_MODE_GROUP} role="group" aria-label={`Payment ${index + 1} input type`}>
                          <button
                            type="button"
                            className={paymentModeButtonClass(inputMode === "percent")}
                            onClick={() => setDepositInputMode(dep.id, "percent")}
                            data-testid={`invoice-create-payment-mode-percent-${dep.id}`}
                          >
                            %
                          </button>
                          <button
                            type="button"
                            className={paymentModeButtonClass(inputMode === "amount")}
                            onClick={() => setDepositInputMode(dep.id, "amount")}
                            data-testid={`invoice-create-payment-mode-amount-${dep.id}`}
                          >
                            $
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <Label className="t-meta text-[10px] block mb-1">
                          {inputMode === "percent" ? "Percentage" : "Amount (USD)"}
                        </Label>
                        <input
                          type="number"
                          min="0"
                          max={inputMode === "percent" ? "100" : undefined}
                          step={inputMode === "percent" ? "0.1" : "0.01"}
                          value={inputMode === "percent" ? (dep.percent ?? "") : (dep.amount ?? "")}
                          onChange={(e) =>
                            updateDeposit(dep.id, inputMode, e.target.value)
                          }
                          className={PAYMENT_COMPACT_INPUT}
                          data-testid={
                            inputMode === "percent"
                              ? `invoice-create-payment-percent-${dep.id}`
                              : `invoice-create-payment-amount-${dep.id}`
                          }
                        />
                      </div>
                    </div>

                    <p className="t-meta text-[10px]">
                      {inputMode === "percent"
                        ? `${formatPercentDisplay(dep.percent)} → ${formatUsd(dep.amount)}`
                        : `${formatUsd(dep.amount)} → ${formatPercentDisplay(dep.percent)}`}
                      {parsedInvoiceAmount > 0 ? "" : " · set invoice amount above"}
                    </p>

                    <div>
                      <Label className="t-meta text-[10px] block mb-1">Due</Label>
                      <InlineDatePickerField
                        value={(dep.due || "").slice(0, 10)}
                        onChange={(next) => updateDeposit(dep.id, "due", next || issued)}
                        placeholder="Select date"
                        triggerClassName={`${DATE_PICKER_TRIGGER} !min-h-[36px] !h-9 w-full`}
                        expanded={openDateField === dep.id}
                        onExpandedChange={(next) => setOpenDateField(next ? dep.id : null)}
                        detachPanel
                        data-testid={`invoice-create-payment-due-${dep.id}`}
                      />
                    </div>
                    {openDateField === dep.id ? (
                      <InlineDatePickerPanel
                        value={(dep.due || "").slice(0, 10)}
                        onChange={(next) => updateDeposit(dep.id, "due", next || issued)}
                        onClose={() => setOpenDateField(null)}
                        className="mt-1"
                        data-testid={`invoice-create-payment-due-${dep.id}-panel`}
                      />
                    ) : null}
                  </li>
                  );
                })}
              </ul>
              <Button
                type="button"
                variant="outline"
                className={`mt-3 w-full ${DIALOG_BTN}`}
                onClick={handleAddPayment}
                disabled={saving}
                data-testid="invoice-create-add-payment"
              >
                <Plus size={16} strokeWidth={1.4} className="mr-2" />
                Add payment
              </Button>
            </div>
          ) : null}

          {error ? <p className="text-sm text-[var(--t-error)]">{error}</p> : null}

        </div>

        <div className="flex items-center justify-end gap-3 border-t border-[var(--t-border)] px-6 py-4">
          <button type="button" onClick={() => onOpenChange(false)} className="t-btn-secondary !rounded-xl">
            Cancel
          </button>
          <button
            type="button"
            className="neo-cta"
            disabled={saving || loadingClients}
            onClick={handleCreate}
            data-testid="invoice-create-submit"
          >
            {saving ? "Creating…" : "Add"}
          </button>
        </div>
      </motion.aside>
    </div>
  );
}

export function InvoiceCreateTrigger({ onClick, disabled }) {
  return (
    <Button
      type="button"
      size="sm"
      className={`!rounded-[var(--t-event-pill-radius)] h-8 px-2.5 gap-1.5 ${DIALOG_BTN}`}
      onClick={onClick}
      disabled={disabled}
      data-testid="revenue-add-invoice"
      title="Add invoice"
    >
      <Plus size={14} />
      <span className="hidden sm:inline">Add invoice</span>
    </Button>
  );
}
